import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '@tg/app-core';
import { blockHas, blockTypeLabel, clientLine, fmtLabel, isTextBlock, textLines, type Block, type Line, type Maxes, type WorkoutContent } from '@tg/domain';
import { useCatalog } from '@tg/app-core';
import { usePageTitle } from '../shell/Shell';
import { isMobile, useSmartSticky } from '../shell/Mobile';
import { ClientPicker, WhoLabel, useWhoList } from '../ui/ClientPicker';
import { InviteLink } from '../ui/InviteLink';
import { LimitAlert } from '../ui/LimitAlert';
import { toast } from '../ui/Toast';
import { Svg } from '../constructor/Svg';
import { DAYICON, ICON } from '../constructor/icons';
import { MONTHS_N, RU, addDays, dm, dowMon, monthEnd, shiftMonth, today } from '../constructor/dates';
import { GroupConflicts, type Conflict, type Decisions } from '../constructor/GroupConflicts';
import { isOnline } from '../offline/net';
import { pendingOps, run, seenRev } from '../offline/outbox';
import { dayKey } from '../offline/days';
import { Bulk, type BulkApi } from './Bulk';

export type Who = { clientId: string } | { groupId: string };
type View = 'compact' | 'full' | 'detail';
type DayRow = { id: string; date: string; content: WorkoutContent; work: WorkoutContent | null; status: 'draft' | 'published'; fromGroupId: string | null; edited: boolean; rev?: number; stat: { has: number; total: number } | null };

const GRPICON =
  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="5.2" r="2.2"/><path d="M1.8 13.2c0-2.4 1.9-4.2 4.2-4.2s4.2 1.8 4.2 4.2"/><path d="M10.6 3.3a2.1 2.1 0 0 1 0 4M12.2 9.4c1.2.5 2 1.8 2 3.4"/></svg>';
const CHATICON =
  '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M13.5 7.6a5.2 5.2 0 0 1-7.4 4.7L2.5 13.2l1-3.6A5.2 5.2 0 1 1 13.5 7.6z"/></svg>';
const DAYST = { rest: 'Отдых', draft: 'Черновик — клиент не видит', pub: 'Опубликована — клиент видит', comp: 'Соревнование' } as const;

/* Вид общий для календаря и конструктора, запоминается и задаётся ссылкой ?view= (CAL-3) */
function useView(): [View, (v: View) => void] {
  const [sp, setSp] = useSearchParams();
  const [v, setV] = useState<View>(() => {
    const q = sp.get('view');
    if (q === 'compact' || q === 'full' || q === 'detail') return q;
    try {
      const s = localStorage.getItem('tg.laneView');
      return s === 'compact' || s === 'detail' ? s : 'full';
    } catch {
      return 'full';
    }
  });
  useEffect(() => {
    if (sp.get('view')) setSp((p) => (p.delete('view'), p), { replace: true });
  }, []);
  return [
    v,
    (n) => {
      setV(n);
      try {
        localStorage.setItem('tg.laneView', n);
      } catch {
        /* нет хранилища */
      }
    },
  ];
}

export function Calendar() {
  /* Телефон: шапка календаря прячется при листании вниз и возвращается при листании вверх */
  useSmartSticky('.calwrap .wkh');
  usePageTitle('Календарь');
  const [sp, setSp] = useSearchParams();
  const W = useWhoList();
  const nav = useNavigate();
  const utils = trpc.useUtils();
  const catalog = useCatalog();
  const [view, setView] = useView();

  /* Клиент или группа: из ссылки, иначе последний открытый (только если он ещё существует
     у ЭТОГО тренера — tg.curWho общий на браузер, а не на аккаунт: после входа другим
     тренером на том же устройстве чужой id там перебивал честный «первый из списка»
     и календарь ошибочно показывал «нет клиентов»), иначе первый. */
  const stored = lastWho();
  const id = sp.get('group') || sp.get('client') || (stored && W.byId(stored) ? stored : null) || W.clients[0]?.id || null;
  const cur = W.byId(id);
  useEffect(() => {
    if (cur && !sp.get('group') && !sp.get('client')) setSp((p) => (p.set(cur.kind === 'group' ? 'group' : 'client', cur.id), p), { replace: true });
    if (cur) rememberWho(cur.id);
  }, [cur?.id]);
  const who: Who | null = cur ? (cur.kind === 'group' ? { groupId: cur.id } : { clientId: cur.id }) : null;
  const isGroup = cur?.kind === 'group';
  const groupNames = useMemo(() => Object.fromEntries(W.groups.map((g) => [g.id, g.name])), [W.groups]);
  const maxes = trpc.trainer.maxesFor.useQuery(who!, { enabled: !!who && !isGroup });
  const pm: Maxes = (who && 'clientId' in who && maxes.data?.[who.clientId]) || {};
  const till = cur ? (cur.kind === 'client' ? cur.client.composedTill : null) : null;

  /* Лента месяцев: от месяца до сегодняшнего до трёх месяцев вперёд, дорастает при прокрутке */
  const t = today();
  const [range, setRange] = useState(() => ({ from: shiftMonth(t.slice(0, 7), -2), to: shiftMonth(t.slice(0, 7), 3) }));
  const months = useMemo(() => {
    const out: string[] = [];
    for (let m = range.from; m <= range.to; m = shiftMonth(m, 1)) out.push(m);
    return out;
  }, [range]);

  const top = useRef<HTMLDivElement>(null);
  const bot = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLElement>(null);
  const scrolled = useRef(false);
  useLayoutEffect(() => {
    const h = wrap.current?.querySelector<HTMLElement>('.wkh');
    if (h) wrap.current!.style.setProperty('--wkh', h.offsetHeight + 'px');
  });
  /* К сегодняшнему месяцу — один раз после первой отрисовки */
  useEffect(() => {
    if (scrolled.current || !cur) return;
    scrolled.current = true;
    /* Месяцы догружаются и меняют высоту — выравниваемся ещё раз, пока тренер сам не начал листать */
    let user = false;
    const mark = () => (user = true);
    addEventListener('wheel', mark, { once: true });
    addEventListener('touchmove', mark, { once: true });
    [50, 350, 900, 1600].forEach((ms) => setTimeout(() => !user && scrollToMonth(t.slice(0, 7), false), ms));
  }, [cur]);
  useEffect(() => {
    const io = new IntersectionObserver(
      (es) => {
        for (const e of es) {
          if (!e.isIntersecting || !scrolled.current) continue;
          if (e.target === bot.current) setRange((r) => ({ ...r, to: shiftMonth(r.to, 1) }));
          if (e.target === top.current) {
            const h0 = document.documentElement.scrollHeight;
            setRange((r) => ({ ...r, from: shiftMonth(r.from, -1) }));
            requestAnimationFrame(() => window.scrollBy(0, document.documentElement.scrollHeight - h0));
          }
        }
      },
      { rootMargin: '400px 0px' },
    );
    if (top.current) io.observe(top.current);
    if (bot.current) io.observe(bot.current);
    return () => io.disconnect();
  }, [cur?.id]);

  const [picker, setPicker] = useState<HTMLElement | null>(null);
  const [jump, setJump] = useState<{ el: HTMLElement } | null>(null);
  const [jumpDate, setJumpDate] = useState(t);
  const jumpTo = (d: string) => {
    const ym = d.slice(0, 7);
    if (ym < range.from) setRange((r) => ({ ...r, from: shiftMonth(ym, -1) }));
    if (ym > range.to) setRange((r) => ({ ...r, to: shiftMonth(ym, 1) }));
    setTimeout(() => {
      const cell = document.querySelector<HTMLElement>(`.day[data-date="${d}"]`);
      if (!cell) return scrollToMonth(ym, true);
      window.scrollTo({ top: cell.getBoundingClientRect().top + scrollY - head() - 56, behavior: 'smooth' });
      cell.classList.add('flash');
      setTimeout(() => cell.classList.remove('flash'), 1600);
    }, 80);
  };

  /* Публикация одним нажатием прямо из календаря (CAL-2); у группы — через окно занятых дней */
  const [conflicts, setConflicts] = useState<{ list: Conflict[]; resolve: (d: Decisions | null) => void } | null>(null);
  const decide = async (dates: string[], publish: boolean) => {
    if (!who || !('groupId' in who) || !publish) return undefined;
    const list = await utils.trainer.groupConflicts.fetch({ groupId: who.groupId, dates });
    if (!list.length) return undefined;
    return new Promise<Decisions | null>((resolve) => setConflicts({ list, resolve }));
  };
  const togglePub = async (d: DayRow) => {
    if (!who) return;
    const on = d.status !== 'published';
    /* Публикация в группу проверяет дни участников на сервере — без сети её не сделать */
    if (isGroup && on && !isOnline()) return toast('Публикация в группу — когда появится интернет: нужно проверить дни участников');
    const decisions = await decide([d.date], on);
    if (decisions === null) return;
    /* Без сети — в очередь на устройстве (offline/outbox), иначе сразу на сервер, как раньше */
    seenRev(dayKey(who, d.date), d.rev);
    await run({ op: 'setStatus', input: { who, date: d.date, published: on, decisions } });
    await utils.trainer.days.invalidate();
    if (!isOnline() || pendingOps().length) toast(on ? 'Опубликуется, когда появится интернет' : 'Станет черновиком, когда появится интернет');
    else toast(on ? (isGroup ? 'Опубликована — участники видят' : 'Опубликована — клиент видит') : isGroup ? 'Скрыта от участников — черновик' : 'Скрыта от клиента — черновик');
  };

  const bulk = useRef<BulkApi>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [picking, setPicking] = useState(false);
  useEffect(() => setSel(new Set()), [cur?.id]);

  if (!cur || !who) {
    if (!W.ready) return null;
    return (
      <div className="cons one">
        <main>
          <section className="calwrap">
            <div className="empty">
              <div className="t">Нет предстоящих тренировок</div>
              <div className="d">Календарю некого показывать — пока нет клиентов. Отправьте ссылку-приглашение, и здесь появится расписание</div>
              <InviteLink />
            </div>
          </section>
        </main>
      </div>
    );
  }

  const ctxCell = { who, isGroup, view, groupNames, pm, catalog, till, sel, picking, togglePub, onBox: (d: string, shift: boolean) => bulk.current?.toggle(d, shift), onPick: (d: string) => bulk.current?.picked(d) };

  return (
    <div className="cons one">
      <main>
        <section className="calwrap" id="cal" ref={wrap}>
          {cur.kind === 'client' && cur.client.overLimit && <LimitAlert clientId={cur.id} />}
          <div className="wkh one">
            <button className="cliSel" id="cli" title="Сменить клиента или группу" onClick={(e) => setPicker(e.currentTarget)}>
              <WhoLabel who={cur} />
              <Svg src={ICON.chev} />
            </button>
            <div className="dates">
              <button className="btn gh sm" id="mToday" title="Пролистать к сегодняшнему дню" onClick={() => (setJumpDate(t), jumpTo(t))}>
                Сегодня
              </button>
              <button className="btn gh sm" id="mMonth" title="Пролистать к началу текущего месяца" onClick={() => scrollToMonth(t.slice(0, 7), true)}>
                Текущий месяц
              </button>
              <button className="btn gh sm jumpb" id="jumpBtn" title="Перейти к дате" onClick={(e) => setJump({ el: e.currentTarget })}>
                <Svg src={ICON.cal} />
                <span>
                  {dm(jumpDate)}.{jumpDate.slice(0, 4)}
                </span>
                <Svg src={ICON.chev} />
              </button>
            </div>
            <div className="views">
              <span className="vtog" title="Вид">
                {(
                  [
                    ['compact', ICON.vcompact, 'Свёрнуто — дата, статус и название'],
                    ['full', ICON.vfull, 'Блоки — что внутри тренировки'],
                    ['detail', ICON.vdetail, 'Полностью — блоки, упражнения, подходы и веса'],
                  ] as const
                ).map(([k, ic, tt]) => (
                  <button key={k} data-view={k} className={view === k ? 'on' : ''} title={tt} onClick={() => setView(k)}>
                    <Svg src={ic} />
                  </button>
                ))}
              </span>
            </div>
          </div>
          <div id="topSent" ref={top}></div>
          <div id="feed">
            {months.map((m) => (
              <Month key={m} ym={m} c={ctxCell} />
            ))}
          </div>
          <div id="botSent" ref={bot}></div>
        </section>
      </main>
      {picker && (
        <ClientPicker
          anchor={picker}
          curId={cur.id}
          opts={{}}
          onPick={(w) => {
            setPicker(null);
            setSp((p) => {
              p.delete('client');
              p.delete('group');
              p.set(w.kind === 'group' ? 'group' : 'client', w.id);
              return p;
            });
          }}
          onClose={() => setPicker(null)}
        />
      )}
      {jump && (
        <DatePicker
          anchor={jump.el}
          value={jumpDate}
          onPick={(d) => {
            setJump(null);
            setJumpDate(d);
            jumpTo(d);
          }}
          onClose={() => setJump(null)}
        />
      )}
      <Bulk ref={bulk} who={who} whoName={cur.name} isGroup={isGroup} sel={sel} setSel={setSel} picking={picking} setPicking={setPicking} decide={decide} />
      {conflicts && (
        <GroupConflicts
          list={conflicts.list}
          names={Object.fromEntries(W.clients.map((c) => [c.id, c.name]))}
          onDone={(d) => {
            conflicts.resolve(d);
            setConflicts(null);
          }}
        />
      )}
      <span hidden>{nav.length}</span>
    </div>
  );
}

type CellCtx = {
  who: Who;
  isGroup: boolean;
  view: View;
  groupNames: Record<string, string>;
  pm: Maxes;
  catalog: ReturnType<typeof useCatalog>;
  till: string | null;
  sel: Set<string>;
  picking: boolean;
  togglePub: (d: DayRow) => void;
  onBox: (d: string, shift: boolean) => void;
  onPick: (d: string) => void;
};

function Month({ ym, c }: { ym: string; c: CellCtx }) {
  const q = trpc.trainer.days.useQuery({ who: c.who, from: ym + '-01', to: monthEnd(ym + '-01') }, { staleTime: 30_000 });
  const days = new Map((q.data as DayRow[] | undefined)?.map((d) => [d.date, d]) || []);
  const [y, m] = ym.split('-').map(Number);
  const first = ym + '-01';
  const lead = dowMon(first);
  const dim = +monthEnd(first).slice(8);
  const nCells = Math.ceil((lead + dim) / 7) * 7;
  const t = today();
  const sub = 'clientId' in c.who ? 'client=' + c.who.clientId : 'group=' + c.who.groupId;
  const cells = [];
  for (let k = 0; k < nCells; k++) {
    const di = k - lead + 1;
    if (di < 1 || di > dim) {
      cells.push(<span key={k} className="day void"></span>);
      continue;
    }
    const date = ym + '-' + String(di).padStart(2, '0');
    cells.push(<DayCell key={date} date={date} d={days.get(date) ?? null} c={c} href={`/constructor?${sub}&date=${date}`} isToday={date === t} />);
  }
  return (
    <section className="mon" data-ym={first}>
      <div className="monh">
        <b>
          {MONTHS_N[m - 1]} {y}
        </b>
        <div className="dow">
          {RU.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>
      </div>
      <div className={`days cal ${c.view === 'compact' ? 'compact' : ''} ${c.view === 'detail' ? 'detail' : ''}`}>{cells}</div>
    </section>
  );
}

const restCell = (
  <span className="stcell rest" title="Отдых">
    <img src="/icons/rest.png" alt="Отдых" />
  </span>
);

function DayCell({ date, d, c, href, isToday }: { date: string; d: DayRow | null; c: CellCtx; href: string; isToday: boolean }) {
  const x = d ? d.content : null;
  const has = !!x && x.blocks.some(blockHas);
  const st: keyof typeof DAYST = !x || (!has && !x.comp) ? 'rest' : x.comp ? 'comp' : d!.status === 'published' ? 'pub' : 'draft';
  const head = (box: boolean) => (
    <span className="d">
      {box && (
        <i
          className={'bk-box' + (c.sel.has(date) ? ' on' : '')}
          data-bk={date}
          role="checkbox"
          aria-checked={c.sel.has(date)}
          aria-label="Выбрать день"
          onMouseDown={(e) => e.shiftKey && e.preventDefault()}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            c.onBox(date, e.shiftKey);
          }}
        >
          <Svg src={'<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3.5 8.5l3 3 6-7"/></svg>'} />
        </i>
      )}
      <s>{RU[dowMon(date)]}</s>
      {+date.slice(8)}
      {isToday && <i className="tdot" title="Сегодня"></i>}
    </span>
  );
  const cls = isToday ? ' today' : '';
  const nav = useNavigate();
  const go = (e: React.MouseEvent) => {
    if (c.picking) {
      e.preventDefault();
      c.onPick(date);
    }
  };
  /* Дни за последним составленным — «Составить этот день» */
  if (st === 'rest') {
    const empty = !d && (!c.till || date > c.till);
    return (
      <Link className={`day ${empty ? 'empty ' : ''}rest${cls}`} to={href} data-date={date} title={empty ? 'Составить этот день' : undefined} onClick={go}>
        {head(false)}
        {restCell}
      </Link>
    );
  }
  const title = st === 'comp' ? x!.title || 'Соревнование' : x!.title || 'Без названия';
  const mark =
    st === 'comp' ? (
      <i className="dmark comp" title={DAYST.comp}>
        <Svg src={DAYICON.comp} />
      </i>
    ) : (
      <i
        className={'dmark ' + st}
        role="button"
        tabIndex={0}
        title={`${DAYST[st]} · нажмите, чтобы ${st === 'draft' ? 'опубликовать' : 'скрыть от клиента'}`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          c.togglePub(d!);
        }}
      >
        <Svg src={DAYICON[st]} />
      </i>
    );
  const tag = c.isGroup ? (
    d!.stat && d!.stat.has < d!.stat.total ? (
      <span className="gtags">
        <span className="gtag busy" title={`Тренировка стоит у ${d!.stat.has} из ${d!.stat.total} участников: у остальных на этот день своя тренировка или они вступили позже`}>
          <Svg src={GRPICON} />
          <b className="lg">{d!.stat.has ? 'у ' + d!.stat.has + ' из ' + d!.stat.total : 'ни у кого'}</b>
          <b className="sh">
            {d!.stat.has}/{d!.stat.total}
          </b>
        </span>
      </span>
    ) : null
  ) : d!.fromGroupId && c.groupNames[d!.fromGroupId] ? (
    <span className="gtags">
      <span className="gtag" title={`Тренировка группы «${c.groupNames[d!.fromGroupId]}»`}>
        <Svg src={GRPICON} />
        <b>{c.groupNames[d!.fromGroupId]}</b>
      </span>
    </span>
  ) : null;
  const selCls = c.sel.has(date) ? ' bksel' : '';
  if (c.view === 'compact')
    return (
      <Link className={`day cmp ${st}${cls}${selCls}`} to={href} data-date={date} onClick={go}>
        {head(true)}
        {mark}
        <span className="t">{title}</span>
        {tag}
      </Link>
    );
  void nav;
  return (
    <Link className={`day ${st}${cls}${selCls}`} to={href} data-date={date} onClick={go}>
      {head(true)}
      {mark}
      <span className="t">{title}</span>
      {tag}
      {c.view === 'detail' ? <BlocksDetail x={x!} pm={c.isGroup ? null : c.pm} c={c} /> : <BlocksList x={x!} />}
      {!has && st === 'comp' && (
        <span className="stcell comp">
          <Svg src={DAYICON.comp} />
          <s>Соревнование</s>
        </span>
      )}
    </Link>
  );
}

const blockName = (b: Block) => b.title || blockTypeLabel(b) || (isTextBlock(b) ? (textLines(b.text)[0] || '').replace(/^[•\-–—*][ \t]+/, '').replace(/:$/, '') : '');

function BlocksList({ x, max = 5 }: { x: WorkoutContent; max?: number }) {
  const bs = x.blocks.filter(blockHas);
  if (!bs.length) return null;
  return (
    <span className="bl num">
      {bs.slice(0, max).map((b, i) => (
        <i key={b.id}>
          <s>{i + 1}</s>
          <b>{blockName(b) || 'блок'}</b>
        </i>
      ))}
      {bs.length > max && <span className="more">ещё {bs.length - max}</span>}
    </span>
  );
}

/* «Полностью»: сообщение тренера, блоки, упражнения, схема и рабочие веса */
function BlocksDetail({ x, pm, c }: { x: WorkoutContent; pm: Maxes | null; c: CellCtx }) {
  const bs = x.blocks.filter(blockHas);
  if (!bs.length) return null;
  const ex = c.catalog.ex;
  const label = (y: Line) => {
    if (y.t === 'chain') return '';
    if (y.txt) return y.txt;
    const l = y.load;
    const load = !l ? '' : (String(l.from) + (l.to != null ? '–' + l.to : '')).replace(/\./g, ',') + ' ' + (l.kind === 'pct' ? '%' : l.unit);
    return [y.scheme, load].filter(Boolean).join(' · ');
  };
  const row = (y: Line) => {
    if (y.t === 'chain')
      return (
        <div className="bxi bxch" key={y.id}>
          <span>{clientLine(y, pm, ex)}</span>
        </div>
      );
    const e = y.exId ? ex(y.exId) : null;
    const line = clientLine(y, pm, ex);
    const kg = pm && y.load?.kind === 'pct' ? line.match(/([\d,–]+) кг$/)?.[1] : null;
    const lb = label(y);
    return (
      <div className="bxi" key={y.id}>
        <span>{e ? e.ru : y.raw || ''}</span>
        <em>
          {lb}
          {kg && (
            <>
              {lb ? ' · ' : ''}
              <u>{kg}&nbsp;кг</u>
            </>
          )}
        </em>
      </div>
    );
  };
  return (
    <div className="bxs">
      {x.message && (
        <div className="bxmsg" title={x.message}>
          <Svg src={CHATICON} />
          <span>{x.message}</span>
        </div>
      )}
      {bs.map((b, i) => (
        <div className="bx" key={b.id}>
          <div className="bxh">
            <s>{i + 1}</s>
            <b>{b.title || blockTypeLabel(b) || (isTextBlock(b) ? 'Блок текстом' : 'Блок')}</b>
            {b.fmt && b.title && <i>{fmtLabel(b.fmt)}</i>}
          </div>
          {isTextBlock(b)
            ? textLines(b.text).map((l, k) => (
                <div className="bxi bxt" key={k}>
                  <span>{l}</span>
                </div>
              ))
            : b.items.map((y) =>
                y.t === 'set' ? (
                  <div className="bxss" key={y.id}>
                    <div className="bxssh">
                      Сет · {y.rounds} {plural(y.rounds, 'круг', 'круга', 'кругов')}
                      {y.rest ? ' · отдых ' + y.rest : ''}
                    </div>
                    {y.items.map(row)}
                  </div>
                ) : (
                  row(y)
                ),
              )}
        </div>
      ))}
    </div>
  );
}

const plural = (n: number, a: string, b: string, c: string) => {
  const m = n % 10,
    h = n % 100;
  return h >= 11 && h <= 14 ? c : m === 1 ? a : m >= 2 && m <= 4 ? b : c;
};

/* Переход к дате (openDatePicker в nav.js) */
export function DatePicker({ anchor, value, onPick, onClose }: { anchor: HTMLElement; value: string; onPick: (d: string) => void; onClose: () => void }) {
  const [view, setView] = useState(value.slice(0, 7));
  const box = useRef<HTMLDivElement>(null);
  const r = anchor.getBoundingClientRect();
  const t = today();
  useEffect(() => {
    const out = (e: MouseEvent) => box.current && !box.current.contains(e.target as Node) && !anchor.contains(e.target as Node) && onClose();
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    setTimeout(() => document.addEventListener('mousedown', out));
    document.addEventListener('keydown', key);
    return () => (document.removeEventListener('mousedown', out), document.removeEventListener('keydown', key));
  }, []);
  const [y, m] = view.split('-').map(Number);
  const first = view + '-01';
  const start = addDays(first, -dowMon(first));
  const chev = (d: number) => `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="${d < 0 ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'}"/></svg>`;
  return createPortal(
    <div ref={box} className="sug dpick" style={{ top: r.bottom + window.scrollY + 6, left: Math.max(8, r.right - 372) }}>
      <div className="dp-h">
        <button type="button" className="dp-nav" title="Предыдущий месяц" onClick={() => setView(shiftMonth(view, -1))}>
          <Svg src={chev(-1)} />
        </button>
        <b>
          {MONTHS_N[m - 1]} {y}
        </b>
        <button type="button" className="dp-nav" title="Следующий месяц" onClick={() => setView(shiftMonth(view, 1))}>
          <Svg src={chev(1)} />
        </button>
      </div>
      <div className="dp-w">
        {RU.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="dp-g">
        {Array.from({ length: 42 }, (_, k) => {
          const d = addDays(start, k);
          return (
            <button key={d} type="button" className={`dp-d ${d.slice(0, 7) !== view ? 'out' : ''} ${d === t ? 'today' : ''} ${d === value ? 'sel' : ''}`} data-date={d} onClick={() => onPick(d)}>
              {+d.slice(8)}
            </button>
          );
        })}
      </div>
      <div className="dp-f">
        <button type="button" className="btn gh sm" onClick={() => onPick(t)}>
          Сегодня
        </button>
      </div>
    </div>,
    document.body,
  );
}

/* Отступ при прокрутке к месяцу: под шапкой и липкой шапкой календаря. На телефоне шапка календаря
   не липнет постоянно (прячется при листании, useSmartSticky) — под шапкой 52 px липнет заголовок месяца */
const head = () =>
  isMobile()
    ? 52 + (document.querySelector<HTMLElement>('.monh')?.offsetHeight || 40)
    : 58 + (document.querySelector<HTMLElement>('.calwrap .wkh')?.offsetHeight || 62);
function scrollToMonth(ym: string, smooth: boolean) {
  const el = document.querySelector<HTMLElement>(`.mon[data-ym="${ym}-01"]`);
  if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - head(), behavior: smooth ? 'smooth' : 'auto' });
}
function lastWho() {
  try {
    return localStorage.getItem('tg.curWho');
  } catch {
    return null;
  }
}
function rememberWho(id: string) {
  try {
    localStorage.setItem('tg.curWho', id);
  } catch {
    /* нет хранилища */
  }
}
